interface SpecimenIdentifierInput {
  number?: string | null;
}

export function normalizeSpecimenIdentifier(value?: string | null): string {
  return value?.trim() ?? "";
}

function lettersToOrdinal(identifier: string): number {
  return identifier.split("").reduce((value, letter) => {
    return value * 26 + letter.charCodeAt(0) - 64;
  }, 0);
}

function ordinalToLetters(ordinal: number): string {
  let remaining = ordinal;
  let result = "";
  while (remaining > 0) {
    remaining -= 1;
    result = String.fromCharCode(65 + (remaining % 26)) + result;
    remaining = Math.floor(remaining / 26);
  }
  return result;
}

export function getNextSpecimenIdentifier(existing: string[]): string {
  const normalized = existing.map((identifier) =>
    normalizeSpecimenIdentifier(identifier).toUpperCase()
  );
  const alphabetic = normalized.filter((identifier) =>
    /^[A-Z]+$/.test(identifier)
  );
  const highestOrdinal = alphabetic.reduce(
    (highest, identifier) => Math.max(highest, lettersToOrdinal(identifier)),
    0
  );
  const used = new Set(normalized);
  let candidateOrdinal = highestOrdinal + 1;
  while (used.has(ordinalToLetters(candidateOrdinal))) candidateOrdinal += 1;
  return ordinalToLetters(candidateOrdinal);
}

export function getSpecimenIdentifierErrors(
  specimens: SpecimenIdentifierInput[]
): Record<number, string> {
  const identifiers = specimens.map((specimen) =>
    normalizeSpecimenIdentifier(specimen.number)
  );
  const counts = identifiers.reduce<Record<string, number>>(
    (result, identifier) => {
      const key = identifier.toLocaleLowerCase();
      result[key] = (result[key] ?? 0) + 1;
      return result;
    },
    {}
  );

  return identifiers.reduce<Record<number, string>>(
    (errors, identifier, index) => {
      if (!identifier) errors[index] = "Specimen identifier is required";
      else if (identifier.includes(";")) {
        errors[index] = "Specimen identifier cannot contain semicolons";
      } else if (counts[identifier.toLocaleLowerCase()] > 1) {
        errors[index] = "Specimen identifier must be unique within this case";
      }
      return errors;
    },
    {}
  );
}
